<template>
  <v-app id="survey-public-take-app">
    <div class="tf-page">
      <img
        src="https://erp.jciindia.in/lib/assets/images/logo.png"
        class="tf-logo"
        alt="JCI India"
      />
      <div class="tf-footer">Made with <span class="tf-heart">♥</span> by <a href="https://vaagai.org.in" target="_blank">Vaagai Tecknowledge</a></div>
      <div
        class="tf-progress-track"
        v-if="Started && !SubmittedFlag && SurveyTitle"
      >
        <div
          class="tf-progress-fill"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>

      <!-- Loading -->
      <div class="tf-center" v-if="LoadingFlag">
        <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      </div>

      <!-- Invalid link -->
      <div class="tf-center tf-invalid" v-if="!LoadingFlag && !SurveyTitle">
        <div class="tf-card">
          <div class="tf-invalid-icon">
            <v-icon size="56" color="#fff">mdi-link-variant-off</v-icon>
          </div>
          <h2>This survey link is invalid</h2>
          <p>It may have expired or is no longer accepting responses.</p>
        </div>
      </div>

      <template v-if="!LoadingFlag && SurveyTitle">
        <!-- Intro screen -->
        <div class="tf-center tf-intro" v-if="!Started && !SubmittedFlag">
          <div class="tf-intro-inner tf-card">
            <h1>{{ SurveyTitle }}</h1>
            <p v-if="SurveyDescription">{{ SurveyDescription }}</p>
            <button class="tf-ok-btn tf-start-btn" @click="start">
              Start
              <v-icon small class="ml-1">mdi-arrow-right</v-icon>
            </button>
            <div class="tf-time-hint">
              <v-icon size="16" color="#7C86A8">mdi-clock-outline</v-icon>
              Takes ~{{ estimatedMinutes }} min
            </div>
          </div>
        </div>

        <!-- Question flow -->
        <div class="tf-slide-area" v-if="Started && !SubmittedFlag">
          <transition name="tf-slide" mode="out-in">
            <div class="tf-slide" :key="CurrentStepIndex">
              <div class="tf-slide-inner tf-card">
                <div class="tf-qnum">
                  {{ CurrentStepIndex + 1 }}
                  <v-icon small color="#0C95D3">mdi-arrow-right</v-icon>
                </div>

                <!-- Contact: Name -->
                <template v-if="currentStep.type === 'name'">
                  <h2 class="tf-question">
                    What's your full name?
                    <span class="tf-required">*</span>
                  </h2>
                  <input
                    type="text"
                    class="tf-input tf-autofocus"
                    v-model="RespondentName"
                    placeholder="Type your answer here..."
                  />
                </template>

                <!-- Contact: Email -->
                <template v-else-if="currentStep.type === 'email'">
                  <h2 class="tf-question">
                    What's your email address?
                    <span class="tf-required">*</span>
                  </h2>
                  <input
                    type="email"
                    class="tf-input tf-autofocus"
                    v-model="RespondentEmail"
                    placeholder="name@example.com"
                  />
                </template>

                <!-- Contact: Mobile -->
                <template v-else-if="currentStep.type === 'mobile'">
                  <h2 class="tf-question">Your mobile number (optional)</h2>
                  <input
                    type="tel"
                    class="tf-input tf-autofocus"
                    v-model="RespondentMobileNo"
                    placeholder="Type your answer here..."
                  />
                </template>

                <!-- Question -->
                <template v-else-if="currentStep.type === 'question'">
                  <h2 class="tf-question">
                    {{ currentStep.question.QuestionText }}
                    <span
                      class="tf-required"
                      v-if="currentStep.question.Required"
                      >*</span
                    >
                  </h2>

                  <!-- Text / Url -->
                  <input
                    v-if="[1, 9].includes(currentStep.question.QuestionType)"
                    :type="
                      currentStep.question.QuestionType === 9 ? 'url' : 'text'
                    "
                    class="tf-input tf-autofocus"
                    v-model="currentStep.question.Answer"
                    :placeholder="
                      currentStep.question.QuestionType === 9
                        ? 'https://example.com'
                        : 'Type your answer here...'
                    "
                  />

                  <!-- Number -->
                  <input
                    v-if="currentStep.question.QuestionType === 10"
                    type="text"
                    inputmode="decimal"
                    class="tf-input tf-autofocus"
                    :value="currentStep.question.Answer"
                    @keypress="blockNonNumericKey($event)"
                    @input="onNumberInput($event, currentStep.question)"
                    @paste="onNumberPaste($event, currentStep.question)"
                    placeholder="Type your answer here..."
                  />

                  <!-- Textarea -->
                  <textarea
                    v-if="currentStep.question.QuestionType === 2"
                    class="tf-input tf-textarea tf-autofocus"
                    v-model="currentStep.question.Answer"
                    rows="3"
                    placeholder="Type your answer here..."
                  ></textarea>

                  <!-- Dropdown / Radio: choice cards -->
                  <div
                    class="tf-choice-hint"
                    v-if="[3, 7].includes(currentStep.question.QuestionType)"
                  >
                    Select any one
                  </div>
                  <div
                    class="tf-choices"
                    v-if="[3, 7].includes(currentStep.question.QuestionType)"
                  >
                    <div
                      v-for="(option, oi) in currentStep.question.OptionArray"
                      :key="oi"
                      class="tf-choice"
                      :class="{
                        'tf-choice-selected':
                          currentStep.question.Answer === option,
                      }"
                      @click="selectChoice(currentStep.question, option)"
                    >
                      <span class="tf-choice-letter">{{ letterFor(oi) }}</span>
                      <span class="tf-choice-text">{{ option }}</span>
                    </div>
                  </div>

                  <!-- Checkbox: multi-select cards -->
                  <div
                    class="tf-choice-hint"
                    v-if="currentStep.question.QuestionType === 8"
                  >
                    Multiple options can be selected
                  </div>
                  <div
                    class="tf-choices"
                    v-if="currentStep.question.QuestionType === 8"
                  >
                    <div
                      v-for="(option, oi) in currentStep.question.OptionArray"
                      :key="oi"
                      class="tf-choice"
                      :class="{
                        'tf-choice-selected': isChecked(
                          currentStep.question,
                          option
                        ),
                      }"
                      @click="toggleCheckbox(currentStep.question, option)"
                    >
                      <span class="tf-choice-letter">{{ letterFor(oi) }}</span>
                      <span class="tf-choice-text">{{ option }}</span>
                      <v-icon
                        v-if="isChecked(currentStep.question, option)"
                        class="tf-check-icon"
                        small
                        color="#0C95D3"
                        >mdi-check</v-icon
                      >
                    </div>
                  </div>

                  <!-- Date -->
                  <v-menu
                    v-if="currentStep.question.QuestionType === 4"
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <input
                        type="text"
                        class="tf-input"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :value="currentStep.question.Answer"
                        placeholder="Select a date"
                      />
                    </template>
                    <v-date-picker
                      v-model="currentStep.question.Answer"
                      dark
                      color="#0C95D3"
                      @input="dateMenu = false"
                    ></v-date-picker>
                  </v-menu>

                  <!-- Time -->
                  <v-menu
                    v-if="currentStep.question.QuestionType === 6"
                    ref="timeMenuRef"
                    v-model="timeMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <input
                        type="text"
                        class="tf-input"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :value="currentStep.question.Answer"
                        placeholder="Select a time"
                      />
                    </template>
                    <v-time-picker
                      v-model="currentStep.question.Answer"
                      full-width
                      dark
                      color="#0C95D3"
                      @click:minute="
                        $refs.timeMenuRef.save(currentStep.question.Answer)
                      "
                    ></v-time-picker>
                  </v-menu>

                  <!-- File / Image -->
                  <div
                    class="tf-file"
                    v-if="[5, 12].includes(currentStep.question.QuestionType)"
                  >
                    <label class="tf-file-drop" for="tf-file-input">
                      <v-icon size="40" color="#0C95D3">
                        {{
                          currentStep.question.QuestionType === 12
                            ? "mdi-image-plus"
                            : "mdi-paperclip"
                        }}
                      </v-icon>
                      <div
                        class="tf-file-name"
                        v-if="currentStep.question.Answer"
                      >
                        {{ currentStep.question.Answer.name }}
                      </div>
                      <div class="tf-file-placeholder" v-else>
                        Click to
                        {{
                          currentStep.question.QuestionType === 12
                            ? "upload an image"
                            : "upload a file"
                        }}
                      </div>
                    </label>
                    <input
                      id="tf-file-input"
                      type="file"
                      class="tf-file-input-native"
                      :accept="
                        currentStep.question.QuestionType === 12
                          ? 'image/png, image/jpeg, image/bmp'
                          : undefined
                      "
                      @change="onFileChange($event, currentStep.question)"
                    />
                  </div>

                  <!-- Rating -->
                  <div
                    class="tf-rating"
                    v-if="currentStep.question.QuestionType === 11"
                  >
                    <v-rating
                      background-color="#5C6480"
                      color="#0C95D3"
                      v-model="currentStep.question.Answer"
                      hover
                      :length="ratingLength(currentStep.question)"
                      :size="ratingSize(currentStep.question)"
                    ></v-rating>
                  </div>
                </template>

                <div class="tf-error" v-if="ErrorMessage">
                  {{ ErrorMessage }}
                </div>

                <div class="tf-actions">
                  <button
                    class="tf-ok-btn"
                    @click="nextStep"
                    :disabled="SubmitFlag"
                  >
                    <span v-if="!(isLastStep && SubmitFlag)">
                      {{ isLastStep ? "Submit" : "OK" }}
                      <v-icon small class="ml-1" v-if="!isLastStep"
                        >mdi-arrow-right</v-icon
                      >
                      <v-icon small class="ml-1" v-else>mdi-check</v-icon>
                    </span>
                    <span v-else>
                      <b-spinner small variant="light"></b-spinner>
                    </span>
                  </button>
                  <div class="tf-hint">press <kbd>Enter ↵</kbd></div>
                </div>
              </div>
            </div>
          </transition>

          <div class="tf-nav">
            <button
              class="tf-nav-btn"
              :disabled="CurrentStepIndex === 0"
              @click="prevStep"
            >
              <v-icon color="#D7DCEC">mdi-chevron-up</v-icon>
            </button>
            <button class="tf-nav-btn" :disabled="isLastStep" @click="nextStep">
              <v-icon color="#D7DCEC">mdi-chevron-down</v-icon>
            </button>
          </div>

          <div class="tf-counter">
            {{ CurrentStepIndex + 1 }} / {{ totalSteps }}
          </div>
        </div>

        <!-- Thank you -->
        <div class="tf-center tf-thankyou" v-if="SubmittedFlag">
          <div class="tf-card">
            <div class="tf-thankyou-icon">
              <v-icon size="56" color="#fff">mdi-check-bold</v-icon>
            </div>
            <h1>Thank you!</h1>
            <p>Your response has been submitted successfully.</p>
          </div>
        </div>
      </template>
    </div>
  </v-app>
</template>

<script>
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  name: "SurveyPublicTake",
  data() {
    return {
      LoadingFlag: true,
      SubmitFlag: false,
      SubmittedFlag: false,
      Started: false,

      CurrentStepIndex: 0,
      ErrorMessage: "",
      dateMenu: false,
      timeMenu: false,

      Code: this.$route.params.code,
      SurveyId: null,
      SurveyTitle: "",
      SurveyDescription: "",
      Questions: [],

      RespondentName: "",
      RespondentEmail: "",
      RespondentMobileNo: "",
    };
  },
  computed: {
    steps() {
      var questionSteps = this.Questions.map(function (question) {
        return { type: "question", question: question };
      });
      return [{ type: "name" }, { type: "email" }, { type: "mobile" }].concat(
        questionSteps
      );
    },
    totalSteps() {
      return this.steps.length;
    },
    currentStep() {
      return this.steps[this.CurrentStepIndex] || {};
    },
    isLastStep() {
      return this.CurrentStepIndex === this.totalSteps - 1;
    },
    progressPercent() {
      if (this.totalSteps === 0) {
        return 0;
      }
      return Math.round((this.CurrentStepIndex / this.totalSteps) * 100);
    },
    estimatedMinutes() {
      return Math.max(1, Math.ceil((this.totalSteps * 20) / 60));
    },
  },
  methods: {
    getSurvey() {
      var server_url = companyConfig.apiURL;
      const thisIns = this;

      this.$http
        .post(server_url + "api/survey/public/show", { Code: this.Code })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          var flag = response.data.flag;
          var records = response.data.records;
          if (flag == 1) {
            thisIns.SurveyId = records.SurveyId;
            thisIns.SurveyTitle = records.SurveyTitle;
            thisIns.SurveyDescription = records.SurveyDescription;
            thisIns.Questions = (records.Questions || []).map(function (q) {
              q.Answer = q.QuestionType == 8 ? [] : null;
              return q;
            });
          }
        })
        .catch(function () {
          thisIns.LoadingFlag = false;
        });
    },
    letterFor(index) {
      return String.fromCharCode(65 + index);
    },
    ratingLength(question) {
      if (question.OptionArray && question.OptionArray.length > 0) {
        return question.OptionArray.length;
      }
      return 5;
    },
    ratingSize(question) {
      var length = this.ratingLength(question);
      if (length > 7) {
        return 28;
      }
      if (length > 5) {
        return 34;
      }
      return 42;
    },
    isChecked(question, option) {
      return (
        Array.isArray(question.Answer) && question.Answer.indexOf(option) !== -1
      );
    },
    toggleCheckbox(question, option) {
      if (!Array.isArray(question.Answer)) {
        this.$set(question, "Answer", []);
      }
      var idx = question.Answer.indexOf(option);
      if (idx === -1) {
        question.Answer.push(option);
      } else {
        question.Answer.splice(idx, 1);
      }
    },
    selectChoice(question, option) {
      question.Answer = option;
      var self = this;
      setTimeout(function () {
        self.nextStep();
      }, 250);
    },
    onFileChange(event, question) {
      question.Answer = event.target.files[0] || null;
    },
    blockNonNumericKey(e) {
      var char = String.fromCharCode(e.which || e.keyCode);
      if (!/[0-9.-]/.test(char)) {
        e.preventDefault();
      }
    },
    sanitizeNumber(raw) {
      var cleaned = (raw || "").replace(/[^0-9.-]/g, "");
      cleaned = cleaned.replace(/(?!^)-/g, "");
      cleaned = cleaned.replace(/(\..*)\./g, "$1");
      return cleaned;
    },
    onNumberInput(event, question) {
      var cleaned = this.sanitizeNumber(event.target.value);
      question.Answer = cleaned;
      if (event.target.value !== cleaned) {
        event.target.value = cleaned;
      }
    },
    onNumberPaste(event, question) {
      event.preventDefault();
      var clipboard = event.clipboardData || window.clipboardData;
      var pasted = clipboard ? clipboard.getData("text") : "";
      var current = event.target.value;
      var start = event.target.selectionStart;
      var end = event.target.selectionEnd;
      var merged =
        current.slice(0, start) + pasted + current.slice(end);
      var cleaned = this.sanitizeNumber(merged);
      question.Answer = cleaned;
      event.target.value = cleaned;
    },
    focusCurrent() {
      this.$nextTick(function () {
        var el = document.querySelector(".tf-autofocus");
        if (el) {
          el.focus();
        }
      });
    },
    start() {
      this.Started = true;
      this.CurrentStepIndex = 0;
      this.ErrorMessage = "";
      this.focusCurrent();
    },
    validateCurrentStep() {
      var step = this.currentStep;
      this.ErrorMessage = "";

      if (step.type === "name") {
        if (!this.RespondentName) {
          this.ErrorMessage = "Please enter your name";
          return false;
        }
      } else if (step.type === "email") {
        if (!this.RespondentEmail) {
          this.ErrorMessage = "Please enter your email";
          return false;
        }
        if (!/.+@.+\..+/.test(this.RespondentEmail)) {
          this.ErrorMessage = "Please enter a valid email address";
          return false;
        }
      } else if (step.type === "question") {
        var q = step.question;
        if (q.Required) {
          if (q.QuestionType == 8) {
            if (!q.Answer || q.Answer.length === 0) {
              this.ErrorMessage = "Please select at least one option";
              return false;
            }
          } else if (!q.Answer) {
            this.ErrorMessage = "This question requires an answer";
            return false;
          }
        }
        if (
          (q.QuestionType == 5 || q.QuestionType == 12) &&
          q.Answer &&
          q.MaxSize
        ) {
          if (q.Answer.size > q.MaxSize * 1000000) {
            this.ErrorMessage =
              "File size should be less than " + q.MaxSize + " MB";
            return false;
          }
        }
      }
      return true;
    },
    nextStep() {
      if (this.SubmitFlag) {
        return;
      }
      if (!this.validateCurrentStep()) {
        return;
      }
      if (this.isLastStep) {
        this.submitForm();
        return;
      }
      this.CurrentStepIndex++;
      this.ErrorMessage = "";
      this.focusCurrent();
    },
    prevStep() {
      if (this.CurrentStepIndex === 0) {
        return;
      }
      this.CurrentStepIndex--;
      this.ErrorMessage = "";
      this.focusCurrent();
    },
    handleKeydown(e) {
      if (e.key !== "Enter") {
        return;
      }
      if (e.target && e.target.tagName === "BUTTON") {
        return;
      }
      if (
        !this.LoadingFlag &&
        this.SurveyTitle &&
        !this.Started &&
        !this.SubmittedFlag
      ) {
        e.preventDefault();
        this.start();
        return;
      }
      if (!this.Started || this.SubmittedFlag) {
        return;
      }
      if (e.target && e.target.tagName === "TEXTAREA" && e.shiftKey) {
        return;
      }
      e.preventDefault();
      this.nextStep();
    },
    submitForm() {
      this.SubmitFlag = true;

      var isFileQuestion = function (q) {
        return q.QuestionType == 5 || q.QuestionType == 12;
      };

      var hasFiles = this.Questions.some(function (q) {
        return isFileQuestion(q) && q.Answer;
      });

      var answers = this.Questions.map(function (q) {
        return {
          QuestionId: q.QuestionId,
          Answer: isFileQuestion(q) ? null : q.Answer,
        };
      });

      var server_url = companyConfig.apiURL;
      const thisIns = this;

      var request;

      if (hasFiles) {
        var formData = new FormData();
        formData.append("Code", this.Code);
        formData.append("SurveyId", this.SurveyId);
        formData.append("RespondentName", this.RespondentName);
        formData.append("RespondentEmail", this.RespondentEmail);
        formData.append("RespondentMobileNo", this.RespondentMobileNo);
        formData.append("Answers", JSON.stringify(answers));

        this.Questions.forEach(function (q) {
          if (isFileQuestion(q) && q.Answer) {
            formData.append("QuestionFile_" + q.QuestionId, q.Answer);
          }
        });

        request = this.$http.post(
          server_url + "api/survey/public/submit",
          formData
        );
      } else {
        request = this.$http.post(server_url + "api/survey/public/submit", {
          Code: this.Code,
          SurveyId: this.SurveyId,
          RespondentName: this.RespondentName,
          RespondentEmail: this.RespondentEmail,
          RespondentMobileNo: this.RespondentMobileNo,
          Answers: answers,
        });
      }

      request
        .then(function (response) {
          thisIns.SubmitFlag = false;
          var output = response.data.output;
          var flag = response.data.flag;
          if (flag == 1) {
            thisIns.SubmittedFlag = true;
          } else {
            Swal.fire({ icon: "error", title: output });
          }
        })
        .catch(function () {
          thisIns.SubmitFlag = false;
        });
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
  },
  beforeMount() {
    this.getSurvey();
  },
};
</script>

<style lang="scss" scoped>
#survey-public-take-app {
  ::v-deep .v-application--wrap {
    min-height: 100vh;
  }
}

.tf-page {
  --tf-navy: #130f2d;
  --tf-accent: #0c95d3;
  --tf-accent-dark: #0a7ab0;
  --tf-teal: #57bcbb;
  --tf-ink: #ffffff;
  --tf-body: #b9c0d8;
  --tf-muted: #7c86a8;
  --tf-border: rgba(255, 255, 255, 0.14);
  --tf-surface: rgba(255, 255, 255, 0.05);
  --tf-surface-hover: rgba(255, 255, 255, 0.09);

  position: relative;
  min-height: 100vh;
  background: linear-gradient(
      90deg,
      rgba(19, 15, 45, 0.97) 0%,
      rgba(19, 15, 45, 0.86) 40%,
      rgba(19, 15, 45, 0.35) 68%,
      rgba(19, 15, 45, 0.05) 100%
    ),
    url("https://erp.jciindia.in/lib/assets/images/bg_large.png") center right /
      cover no-repeat,
    var(--tf-navy);
  color: var(--tf-ink);
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
  overflow: hidden;
}

.tf-logo {
  position: fixed;
  top: 28px;
  left: clamp(24px, 6vw, 96px);
  height: 30px;
  width: auto;
  filter: brightness(0) invert(1);
  opacity: 0.95;
  z-index: 25;
}

.tf-footer {
  position: fixed;
  left: 0;
  bottom: 8px;
  width: 100%;
  text-align: center;
  color: var(--tf-muted);
  font-size: 1.25rem;
  letter-spacing: 0.01em;
  z-index: 15;
  pointer-events: none;

  a {
    pointer-events: auto;
    color: var(--tf-accent);
    text-decoration: none;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--tf-accent);
      outline-offset: 2px;
    }
  }
}

.tf-heart {
  color: #ff6b81;
}

.tf-progress-track {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  z-index: 20;

  .tf-progress-fill {
    height: 100%;
    background: var(--tf-accent);
    box-shadow: 0 0 12px rgba(12, 149, 211, 0.65);
    transition: width 0.35s ease;
  }
}

.tf-center {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-align: left;
  padding: 24px clamp(24px, 6vw, 96px);
}

.tf-card {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  width: 100%;
}

.tf-invalid,
.tf-thankyou {
  .tf-card {
    max-width: 560px;
  }
  h1,
  h2 {
    font-family: "Poppins", "Roboto", sans-serif;
    font-weight: 700;
    margin: 20px 0 8px;
    color: var(--tf-ink);
  }
  p {
    color: var(--tf-body);
    font-size: 1.1rem;
  }
}

.tf-invalid-icon,
.tf-thankyou-icon {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tf-accent);
  box-shadow: 0 14px 32px -8px rgba(12, 149, 211, 0.6);
  animation: tf-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.tf-intro {
  .tf-intro-inner {
    max-width: 640px;
    text-align: left;

    h1 {
      font-family: "Poppins", "Roboto", sans-serif;
      font-size: clamp(2rem, 4.2vw, 3.4rem);
      font-weight: 800;
      letter-spacing: -0.02em;
      margin-bottom: 20px;
      line-height: 1.15;
      color: var(--tf-ink);
    }

    p {
      font-size: 1.15rem;
      color: var(--tf-body);
      margin-bottom: 32px;
      line-height: 1.65;
    }
  }
}

.tf-time-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  color: var(--tf-muted);
  font-size: 0.9rem;
}

.tf-slide-area {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 24px clamp(24px, 6vw, 96px);
}

.tf-slide {
  width: 100%;
  max-width: 640px;
}

.tf-slide-inner {
  width: 100%;
}

.tf-qnum {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Poppins", "Roboto", sans-serif;
  font-weight: 700;
  color: var(--tf-accent);
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.tf-question {
  font-family: "Poppins", "Roboto", sans-serif;
  font-size: clamp(1.5rem, 3.2vw, 2.3rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
  margin-bottom: 32px;
  color: var(--tf-ink);
}

.tf-required {
  color: var(--tf-accent);
}

.tf-input {
  width: 100%;
  border: none;
  border-bottom: 2px solid var(--tf-border);
  background: transparent;
  font-size: 1.45rem;
  padding: 10px 4px;
  outline: none;
  color: var(--tf-ink);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;

  &:hover {
    border-bottom-color: rgba(255, 255, 255, 0.35);
  }

  &:focus {
    border-bottom-color: var(--tf-accent);
    box-shadow: 0 6px 16px -10px rgba(12, 149, 211, 0.8);
  }

  &::placeholder {
    color: var(--tf-muted);
  }
}

.tf-textarea {
  resize: vertical;
  min-height: 90px;
}

.tf-choice-hint {
  color: var(--tf-muted);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.tf-choices {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.tf-choice {
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1.5px solid var(--tf-border);
  border-radius: 14px;
  padding: 15px 20px;
  cursor: pointer;
  background: var(--tf-surface);
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: rgba(12, 149, 211, 0.55);
    background: var(--tf-surface-hover);
    transform: translateY(-1px);
  }

  &.tf-choice-selected {
    border-color: var(--tf-accent);
    background: rgba(12, 149, 211, 0.16);
    box-shadow: 0 0 0 1px var(--tf-accent);
  }
}

.tf-choice-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  border-radius: 9px;
  font-family: "Poppins", "Roboto", sans-serif;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--tf-body);
  background: rgba(255, 255, 255, 0.09);
  transition: background 0.15s ease, color 0.15s ease;
}

.tf-choice-selected .tf-choice-letter {
  background: var(--tf-accent);
  color: #fff;
}

.tf-choice-text {
  flex: 1;
  font-size: 1.08rem;
  color: var(--tf-ink);
}

.tf-check-icon {
  margin-left: auto;
}

.tf-file {
  margin-top: 8px;
}

.tf-file-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 2px dashed var(--tf-border);
  border-radius: 16px;
  padding: 40px 20px;
  cursor: pointer;
  text-align: center;
  background: var(--tf-surface);
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--tf-accent);
    background: var(--tf-surface-hover);
  }
}

.tf-file-placeholder {
  color: var(--tf-muted);
}

.tf-file-name {
  color: var(--tf-ink);
  font-weight: 600;
}

.tf-file-input-native {
  display: none;
}

.tf-rating {
  margin-top: 8px;

  .tf-rating-labels {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    color: var(--tf-muted);
    font-size: 0.85rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  ::v-deep .v-rating {
    flex-wrap: wrap;
    row-gap: 6px;
  }

  ::v-deep .v-icon {
    padding: 2px;
  }
}

.tf-error {
  color: #ff7a90;
  font-weight: 600;
  margin-top: 14px;
  font-size: 0.95rem;
}

.tf-actions {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 36px;
}

.tf-ok-btn {
  display: inline-flex;
  align-items: center;
  background: var(--tf-accent);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px 30px;
  font-family: "Poppins", "Roboto", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 24px -8px rgba(12, 149, 211, 0.55);
  transition: box-shadow 0.2s ease, transform 0.15s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--tf-accent-dark);
    box-shadow: 0 14px 30px -8px rgba(12, 149, 211, 0.7);
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid var(--tf-accent);
    outline-offset: 3px;
  }
}

.tf-start-btn {
  padding: 15px 38px;
  font-size: 1.05rem;
}

.tf-hint {
  color: var(--tf-muted);
  font-size: 0.88rem;

  kbd {
    display: inline-block;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid var(--tf-border);
    border-bottom: 2px solid rgba(255, 255, 255, 0.22);
    border-radius: 6px;
    padding: 3px 8px;
    font-family: "Courier New", monospace;
    font-size: 0.8rem;
    color: var(--tf-body);
  }
}

.tf-nav {
  position: fixed;
  left: 24px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 20;
}

.tf-nav-btn {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--tf-body);
  transition: background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--tf-surface-hover);
    color: var(--tf-ink);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--tf-accent);
    outline-offset: 2px;
  }
}

.tf-counter {
  position: fixed;
  right: 24px;
  bottom: 24px;
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  color: var(--tf-body);
  font-family: "Poppins", "Roboto", sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 7px 16px;
  border-radius: 20px;
  z-index: 20;
}

.tf-slide-enter-active,
.tf-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.tf-slide-enter {
  opacity: 0;
  transform: translateY(24px);
}

.tf-slide-leave-to {
  opacity: 0;
  transform: translateY(-24px);
}

@keyframes tf-pop {
  from {
    opacity: 0;
    transform: scale(0.75);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tf-slide-enter-active,
  .tf-slide-leave-active,
  .tf-ok-btn,
  .tf-choice,
  .tf-nav-btn {
    transition: none !important;
  }
  .tf-invalid-icon,
  .tf-thankyou-icon {
    animation: none !important;
  }
}

@media (max-width: 600px) {
  .tf-logo {
    height: 22px;
    top: 20px;
  }

  .tf-nav {
    left: 16px;
    bottom: 16px;
  }
  .tf-counter {
    right: 16px;
    bottom: 16px;
  }
}
</style>
